import { Injectable } from '@angular/core';
import { CurrentUser } from '@/core/models/user';
import { AnalyzeFilters } from '@/home/analyze/analyze-filters';
import { DashboardView } from '@/home/dashboard/dashboard.component';
import { AnalyzeGroupBys } from '@/home/analyze/analyze-overview/analyze-overview.component';

/**
 * This class provides methods for accessing local storage data.
 * It is designed to be the central point for all local storage operations.
 * Any local storage access shall be performed via this class by convention.
 */
@Injectable({
    providedIn: 'root',
})
export class LocalStorageService {
    private static readonly LOGO_LAST_MODIFIED_KEY = 'logoLastModified-';
    private static readonly USER_UPLOADED_LOGO_KEY = 'userUploadedLogo-';
    private static readonly USER_UPLOADED_LOGO_KEY_LEGACY = 'userUploadedLogo';
    private static readonly TOKEN_KEY = 'token';
    private static readonly DEFAULT_LOGO_KEY: string = 'defaultLogo';
    private static readonly PROFILE_KEY = 'profile';
    private static readonly TRIAL_STEP_KEY = 'trial-step';
    private static readonly HIDDEN_NEWS_IDS: string = 'hiddenNewsIds';
    private static readonly ANALYZE_FILTERS: string = 'analyzeFilters-';
    private static readonly DASHBOARD_VIEW: string = 'dashboardView-';
    private static readonly ANALYZE_GROUPBYS: string = 'analyzeGroupBys-';

    // //////////////////// DEFAULT LOGO /////////////////////////

    static getDefaultLogoBase64(): string | null {
        return localStorage.getItem(LocalStorageService.DEFAULT_LOGO_KEY);
    }

    static storeDefaultLogoBase64(base64data: string): void {
        localStorage.setItem(LocalStorageService.DEFAULT_LOGO_KEY, base64data);
    }

    // //////////////////// USER UPLOADED LOGO /////////////////////////

    public static clearLegacyLogo(): void {
        if (localStorage.getItem(LocalStorageService.USER_UPLOADED_LOGO_KEY_LEGACY)) {
            localStorage.removeItem(LocalStorageService.USER_UPLOADED_LOGO_KEY_LEGACY);
        }
    }

    public static getUserUploadedLogo(userId: number): string | null {
        return localStorage.getItem(LocalStorageService.USER_UPLOADED_LOGO_KEY + userId);
    }

    public static removeUserUploadedLogo(userId: number): void {
        localStorage.removeItem(LocalStorageService.USER_UPLOADED_LOGO_KEY + userId);
    }

    public static storeUserUploadedLogo(base64logo: string, userId: number | undefined): void {
        localStorage.setItem(LocalStorageService.USER_UPLOADED_LOGO_KEY + userId, base64logo);
    }

    // //////////////////// LOGO LAST MODIFIED  /////////////////////////

    static getLogoLastModified(userId: number): Date | null {
        const localStorageDate = localStorage.getItem(LocalStorageService.LOGO_LAST_MODIFIED_KEY + userId);
        return localStorageDate ? new Date(localStorageDate) : null;
    }

    static storeLogoLastModified(lastModified: string, userId: number): void {
        localStorage.setItem(LocalStorageService.LOGO_LAST_MODIFIED_KEY + userId, lastModified);
    }

    static removeLogoLastModified(userId: number): void {
        localStorage.removeItem(LocalStorageService.LOGO_LAST_MODIFIED_KEY + userId);
    }

    // //////////////////// TOKEN /////////////////////////

    public static getToken(): string | null {
        return localStorage.getItem(LocalStorageService.TOKEN_KEY);
    }

    public static removeToken(): void {
        localStorage.removeItem(LocalStorageService.TOKEN_KEY);
    }

    static storeToken(token: string): void {
        localStorage.setItem(LocalStorageService.TOKEN_KEY, token);
    }

    // //////////////////// PROFILE  /////////////////////////

    public static storeProfile(curUser: CurrentUser): void {
        const json = CurrentUser.toJsonLocalStorage(curUser);
        localStorage.setItem(LocalStorageService.PROFILE_KEY, json);
    }

    public static removeProfile(): void {
        localStorage.removeItem(LocalStorageService.PROFILE_KEY);
    }

    public static getProfile(): CurrentUser | null {
        const profile = localStorage.getItem(LocalStorageService.PROFILE_KEY);
        if (!profile) {
            return null;
        }
        const jsonObj = JSON.parse(profile);
        return CurrentUser.fromJson(jsonObj);
    }

    // //////////////////// Trial Step /////////////////////////

    public static getTrialStep(): string | null {
        return localStorage.getItem(LocalStorageService.TRIAL_STEP_KEY);
    }

    public static storeTrialStep(trialStep: string): void {
        localStorage.setItem(LocalStorageService.TRIAL_STEP_KEY, trialStep);
    }

    static removeTrialStep(): void {
        localStorage.removeItem(LocalStorageService.TRIAL_STEP_KEY);
    }

    // //////////////////// TOKEN /////////////////////////

    public static getHiddenNewsIds(): string | null {
        return localStorage.getItem(LocalStorageService.HIDDEN_NEWS_IDS);
    }

    public static removeHiddenNewsIds(): void {
        localStorage.removeItem(LocalStorageService.HIDDEN_NEWS_IDS);
    }

    static storeHiddenNewsIds(hiddenNewsIds: string): void {
        localStorage.setItem(LocalStorageService.HIDDEN_NEWS_IDS, hiddenNewsIds);
    }

    // //////////////////// Analyze filters  /////////////////////////

    private static getAnalyzeFiltersKey(userId: number): string {
        return LocalStorageService.ANALYZE_FILTERS + userId;
    }

    public static storeAnalyzeFilters(userId: number, filters: AnalyzeFilters): void {
        const json = JSON.stringify(filters);
        localStorage.setItem(this.getAnalyzeFiltersKey(userId), json);
    }

    public static removeAnalyzeFilters(userId: number): void {
        localStorage.removeItem(this.getAnalyzeFiltersKey(userId));
    }

    public static getAnalyzeFilters(userId: number): AnalyzeFilters | null {
        const filterJson = localStorage.getItem(this.getAnalyzeFiltersKey(userId));
        if (!filterJson) {
            return null;
        }
        const filterObj: AnalyzeFilters = JSON.parse(filterJson);
        if (filterObj.dateRange && filterObj.dateRange.length === 2) {
            filterObj.dateRange[0] = new Date(filterObj.dateRange[0]);
            filterObj.dateRange[1] = new Date(filterObj.dateRange[1]);
        }
        // the following check assures that isBilled key always exists, even if undefined
        // !('isBilled' in filterObj) will not work as this will tell TypeScript that filterObj is of a different type
        if (filterObj.isBilled === undefined) {
            filterObj.isBilled = undefined;
        }
        if (filterObj.isNoteAvailable === undefined) {
            filterObj.isNoteAvailable = undefined;
        }
        if (filterObj.dateRangeOption === undefined) {
            filterObj.dateRangeOption = undefined;
        }
        return filterObj;
    }

    // //////////////////// dashboard view  /////////////////////////

    private static getDashboardViewKey(userId: number): string {
        return LocalStorageService.DASHBOARD_VIEW + userId;
    }

    public static storeDashboardView(userId: number, view: DashboardView): void {
        const json = JSON.stringify(view);
        localStorage.setItem(this.getDashboardViewKey(userId), json);
    }

    public static removeDashboardView(userId: number): void {
        localStorage.removeItem(this.getDashboardViewKey(userId));
    }

    public static getDashboardView(userId: number): DashboardView | null {
        const json = localStorage.getItem(this.getDashboardViewKey(userId));
        if (!json) {
            return null;
        }
        const view: DashboardView = JSON.parse(json);
        if (view.dateRange && view.dateRange.length === 2) {
            view.dateRange[0] = new Date(view.dateRange[0]);
            view.dateRange[1] = new Date(view.dateRange[1]);
        }
        return view;
    }

    // //////////////////// grouop by options  /////////////////////////

    private static getAnalyzeGroupBysKey(userId: number): string {
        return LocalStorageService.ANALYZE_GROUPBYS + userId;
    }

    public static storeAnalyzeGroupBys(userId: number, filters: AnalyzeGroupBys): void {
        const json = JSON.stringify(filters);
        localStorage.setItem(this.getAnalyzeGroupBysKey(userId), json);
    }

    public static removeAnalyzeGroupBys(userId: number): void {
        localStorage.removeItem(this.getAnalyzeGroupBysKey(userId));
    }

    public static getAnalyzeGroupBys(userId: number): AnalyzeGroupBys | null {
        const jsonStr = localStorage.getItem(this.getAnalyzeGroupBysKey(userId));
        if (!jsonStr) {
            return null;
        }
        return JSON.parse(jsonStr) as AnalyzeGroupBys;
    }

    // //////////////// general methods  /////////////////////////

    private static removeAll(keyStart: string): void {
        Object.keys(localStorage)
            .filter((key) => key.startsWith(keyStart))
            .forEach((key) => localStorage.removeItem(key));
    }

    public static onLogout(): void {
        this.removeToken();
        this.removeProfile();
        this.removeAll(this.LOGO_LAST_MODIFIED_KEY);
        this.removeAll(this.USER_UPLOADED_LOGO_KEY);
        this.removeAll(this.LOGO_LAST_MODIFIED_KEY);
        this.removeAll(this.ANALYZE_FILTERS);
        this.removeAll(this.DASHBOARD_VIEW);
        this.removeAll(this.ANALYZE_GROUPBYS);
    }
}
